import React, { useEffect, useState } from "react";

/**
 * MileMarker — a US highway shield-shaped badge used as a section anchor.
 * Fully static. Pass `number` as a string so leading zeros are preserved.
 */
export const MileMarker = ({ number = "000", label = "MILE" }) => (
  <div className="mile-marker" data-testid={`mile-marker-${number}`}>
    <span className="mile-top">{label}</span>
    <span className="mile-num">{number}</span>
  </div>
);

/**
 * SectionAnchor — section opener with the mile marker sitting on the road.
 * The vertical painted road passes through the center.
 */
export const SectionAnchor = ({ mile, title, overline, id }) => (
  <div className="relative flex flex-col items-center gap-4 pt-20 pb-10" id={id}>
    <div className="road-track h-20 absolute left-1/2 -translate-x-1/2 top-0">
      <div className="road-vertical" />
    </div>
    <div className="relative z-10 mt-14">
      <MileMarker number={mile} />
    </div>
    {overline && (
      <div className="overline mt-2 text-center" data-testid={`section-overline-${id}`}>
        {overline}
      </div>
    )}
    {title && (
      <h2
        className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-center max-w-3xl px-4"
        data-testid={`section-title-${id}`}
      >
        {title}
      </h2>
    )}
  </div>
);

/**
 * RoadBetween — a vertical painted road segment that lives between two sections.
 * Give it a `heightClass` to tune its length.
 */
export const RoadBetween = ({ heightClass = "h-16", children }) => (
  <div className={`relative w-full flex items-center justify-center ${heightClass}`}>
    <div className="road-track h-full flex items-center justify-center">
      <div className="road-vertical h-full" />
    </div>
    {children}
  </div>
);

/**
 * ScrollMileIndicator — persistent "MILE XXX" pill that tracks scroll depth.
 * On mobile: top-right sticky. On desktop: right rail. Updates with rAF-throttled scroll.
 */
export const ScrollMileIndicator = () => {
  const [mile, setMile] = useState("000");

  useEffect(() => {
    let frame = 0;
    const update = () => {
      const scrollTop = window.scrollY;
      const docHeight = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const pct = Math.min(1, scrollTop / docHeight);
      // 0 → 5000 miles across the whole page
      const m = Math.round(pct * 5000);
      setMile(String(m).padStart(3, "0"));
      frame = 0;
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      className="fixed z-40 top-20 right-3 sm:top-24 sm:right-6 pointer-events-none"
      data-testid="scroll-mile-indicator"
    >
      <div className="mile-pill">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FFC107] animate-pulse" />
        MILE {mile}
      </div>
    </div>
  );
};
